import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Birthday", "Anniversary", "Picnic", "Intimate Dinner", "Balloon Garland", "Catering Only", "Other"];

const initial = { name: "", email: "", phone: "", event_type: "", event_date: "", guests: "", message: "" };

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_type) {
      toast.error("Please choose an event type");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        phone: form.phone || null,
        event_date: form.event_date || null,
        guests: form.guests || null,
      });
      toast.success("Your enquiry has been sent", {
        description: "Thank you — The Diamond Effect Affairs will be in touch shortly. A confirmation is on its way to your inbox.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or email us directly at dannae28tilbanye@gmail.com",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-28 lg:py-36 bg-[#F2D8D5]/20 border-t border-[#E5D3B3]/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="eyebrow"
          >
            Begin Your Affair
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-6 font-serif font-light text-4xl sm:text-5xl leading-tight text-[#2A2423]"
          >
            Tell us about your <em className="text-[#D8A7A1]">celebration</em>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 text-base leading-relaxed text-muted-foreground max-w-md"
          >
            Share a few details and we will craft a proposal — decor, garlands and a bespoke menu designed around you.
            Prefer email? Write to us at{" "}
            <a href="mailto:dannae28tilbanye@gmail.com" data-testid="booking-email-link" className="text-[#D8A7A1] border-b border-[#D8A7A1]/40">
              dannae28tilbanye@gmail.com
            </a>
            .
          </motion.p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="lg:col-span-7 bg-[#FDFBF7] border border-[#E5D3B3] p-8 lg:p-12 space-y-7"
        >
          <div className="grid sm:grid-cols-2 gap-7">
            <div className="space-y-2">
              <Label htmlFor="booking-name" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Name *</Label>
              <Input
                id="booking-name"
                data-testid="booking-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email *</Label>
              <Input
                id="booking-email"
                type="email"
                data-testid="booking-email-input"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-7">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Event Type *</Label>
              <Select value={form.event_type} onValueChange={set("event_type")}>
                <SelectTrigger data-testid="booking-event-type-select" className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus:ring-0">
                  <SelectValue placeholder="Choose your occasion" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`booking-event-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-date" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Preferred Date</Label>
              <Input
                id="booking-date"
                type="date"
                data-testid="booking-date-input"
                value={form.event_date}
                onChange={set("event_date")}
                className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-7">
            <div className="space-y-2">
              <Label htmlFor="booking-phone" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Phone</Label>
              <Input
                id="booking-phone"
                data-testid="booking-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Optional"
                className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-guests" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Guests</Label>
              <Input
                id="booking-guests"
                data-testid="booking-guests-input"
                value={form.guests}
                onChange={set("guests")}
                placeholder="e.g. 20"
                className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="booking-message" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your Vision *</Label>
            <Textarea
              id="booking-message"
              data-testid="booking-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your event — theme, colours, cuisine wishes..."
              className="rounded-none border-0 border-b border-[#E5D3B3] bg-transparent px-0 resize-none focus-visible:ring-0 focus-visible:border-[#D8A7A1]"
            />
          </div>

          <Button
            type="submit"
            data-testid="booking-form-submit"
            disabled={sending}
            className="w-full sm:w-auto rounded-full bg-[#2A2423] text-[#FDFBF7] text-xs uppercase tracking-[0.22em] px-12 py-6 hover:bg-[#D8A7A1] transition-colors duration-300"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </span>
            ) : (
              "Send Enquiry"
            )}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
